import { useState, useCallback } from 'react';
import api from '../../API';
import { uploadFileInChunks } from '../../utils/girderChunkedUpload';

/**
 * Hook that manages file uploads into a single Girder folder for the projects UI.
 *
 * @param {string|null} targetFolderId - Girder folder _id to upload into
 * @param {function} onDone - called after all files finish (success or error)
 * @returns {{ uploads, uploading, uploadFiles, clearUploads }}
 *
 * uploads: Array<{ name: string, progress: number, error: string|null }>
 * uploading: boolean — true while any file is in flight
 * uploadFiles: (FileList|File[]) => void — start uploading the given files
 * clearUploads: () => void — reset the uploads list (e.g. after user dismisses)
 */
export function useProjectUpload(targetFolderId, onDone) {
    const [uploads, setUploads] = useState([]);
    const [uploading, setUploading] = useState(false);

    const uploadFiles = useCallback(async (files) => {
        if (!targetFolderId || !files || files.length === 0) return;

        const fileArray = Array.from(files);
        setUploading(true);
        setUploads(fileArray.map((f) => ({ name: f.name, progress: 0, error: null })));

        for (let i = 0; i < fileArray.length; i++) {
            const file = fileArray[i];
            try {
                // Step 1 — initiate upload, get uploadId from Girder
                const initRes = await api.post('/file', {
                    query: {
                        parentType: 'folder',
                        parentId: targetFolderId,
                        name: file.name,
                        size: String(file.size),
                        mimeType: file.type || 'application/octet-stream',
                    },
                });
                if (!initRes.data?._id) {
                    throw new Error(initRes.data?.message || 'Failed to initiate upload.');
                }
                const upload = initRes.data;

                // Step 2 — send file in chunks
                await uploadFileInChunks(file, upload._id, upload, ({ progress }) => {
                    setUploads((prev) =>
                        prev.map((u, idx) => (idx === i ? { ...u, progress } : u))
                    );
                });

                setUploads((prev) =>
                    prev.map((u, idx) => (idx === i ? { ...u, progress: 100 } : u))
                );
            } catch (err) {
                setUploads((prev) =>
                    prev.map((u, idx) =>
                        idx === i ? { ...u, error: err.message || 'Upload failed.' } : u
                    )
                );
            }
        }

        setUploading(false);
        if (onDone) onDone();
    }, [targetFolderId, onDone]);

    const clearUploads = useCallback(() => setUploads([]), []);

    return { uploads, uploading, uploadFiles, clearUploads };
}
